import { useEffect, useState } from 'react';
import {
  PortalBuildFreshness,
  evaluatePortalBuildFreshness,
  fetchPortalBuildPublished,
  initialPortalBuildFreshness,
  watchPortalBuild,
} from '../providers/portalBuild';

/**
 * E30 · `PortalBuildFreshness` — the read model the epic exists for, held in client state.
 *
 * The hook is deliberately thin: every decision lives in `providers/portalBuild`, which is
 * framework-free and unit-tested against the board's GWTs. All this adds is React's lifecycle —
 * subscribe on mount, unsubscribe on unmount, and ignore an answer that arrives after unmount.
 *
 * Nothing is persisted and nothing is appended: `scope: client / sync: never` on all five E30
 * events. A reload starts from `initialPortalBuildFreshness` again, which is correct — the reloaded
 * tab is running the build it just fetched.
 */
export const usePortalBuildFreshness = (): PortalBuildFreshness => {
  const [freshness, setFreshness] = useState<PortalBuildFreshness>(
    initialPortalBuildFreshness,
  );

  useEffect(() => {
    let mounted = true;

    const check = () => {
      void fetchPortalBuildPublished().then((published) => {
        if (!mounted) {
          return;
        }
        const checkedAt = new Date().toISOString();
        // A failed check returns the previous state BY REFERENCE, so React re-renders nothing and
        // a dismissed prompt is not re-opened by a hosting hiccup.
        setFreshness((previous) =>
          evaluatePortalBuildFreshness(previous, published, checkedAt),
        );
      });
    };

    const stop = watchPortalBuild(check);

    return () => {
      mounted = false;
      stop();
    };
  }, []);

  return freshness;
};
